/**
 * ConsultationSummaryReview.jsx
 * Route: /consultation-summary/:summaryId
 *
 * Staff review screen for a Client-Facing Consultation Summary.
 * Lifecycle: draft → approve → send
 *
 * Design invariants:
 *  • Works for both Leads and Clients — no engagement required
 *  • Staff must approve before sending
 *  • Approved/sent versions are immutable
 *  • Evidence/provenance drawer is staff-only (never shown to client)
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Send, RotateCcw, FileText, Eye,
  EyeOff, ChevronDown, ChevronUp, Loader2, Lock, Mail, Paperclip,
  Info, AlertCircle, Edit3, Save, X, ExternalLink, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ── Constants ─────────────────────────────────────────────────────────────────

const SECTION_LABELS = {
  consultation_overview:            "Consultation Overview",
  background:                        "Your Situation",
  topics_discussed:                 "Topics Discussed",
  questions_raised:                 "Questions Raised",
  guidance_provided:                "Guidance Provided",
  tax_considerations:               "Tax & Compliance Considerations",
  recommendations:                  "Recommended Next Steps",
  future_planning:                  "Future Planning Considerations",
  services_discussed:               "Services Discussed",
  pricing_discussed:                "Pricing Discussed",
  information_needed:               "Information & Documents Needed",
  timing_deadlines:                 "Important Timing & Deadlines",
  additional_follow_up_observation: "Additional Follow-Up Observation",
  disclaimer:                       "Professional Disclaimer",
};

const RENDER_ORDER = [
  "consultation_overview",
  "background",
  "topics_discussed",
  "questions_raised",
  "guidance_provided",
  "tax_considerations",
  "recommendations",
  "future_planning",
  "services_discussed",
  "pricing_discussed",
  "information_needed",
  "timing_deadlines",
];

const STATUS_CONFIG = {
  generating:        { label: "Generating…",      color: "bg-indigo-50 text-indigo-700 border-indigo-200"  },
  generation_failed: { label: "Generation Failed", color: "bg-red-50 text-red-700 border-red-200"          },
  draft:             { label: "Draft",             color: "bg-amber-50 text-amber-700 border-amber-200"     },
  approved:          { label: "Approved",          color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  sent:              { label: "Sent",              color: "bg-blue-50 text-blue-700 border-blue-200"         },
};

// ── Section card ─────────────────────────────────────────────────────────────

function SectionCard({ sectionKey, text, visible, editing, onToggleVisible, onEdit, onSaveEdit, readonly }) {
  const [localText, setLocalText] = useState(text || "");
  const [isEditing, setIsEditing] = useState(false);
  const isEmpty = !(text || "").trim();
  const label = SECTION_LABELS[sectionKey] || sectionKey;

  const startEdit = () => { setLocalText(text || ""); setIsEditing(true); };
  const cancelEdit = () => { setIsEditing(false); setLocalText(text || ""); };
  const saveEdit = () => { onSaveEdit(sectionKey, localText); setIsEditing(false); };

  return (
    <div
      className={cn(
        "rounded-xl border transition-all",
        visible ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60",
      )}
      data-testid={`cs-section-${sectionKey}`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            {label}
          </span>
          {isEmpty && (
            <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-slate-100 text-slate-400 border border-slate-200">
              empty
            </span>
          )}
        </div>
        {!readonly && (
          <div className="flex items-center gap-1.5">
            {!isEditing && !isEmpty && (
              <button
                onClick={startEdit}
                data-testid={`cs-edit-${sectionKey}`}
                className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#2F80ED] transition-colors"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            <button
              onClick={() => onToggleVisible(sectionKey)}
              data-testid={`cs-toggle-${sectionKey}`}
              title={visible ? "Hide from document" : "Show in document"}
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              {visible
                ? <><Eye className="h-3.5 w-3.5" /> Visible</>
                : <><EyeOff className="h-3.5 w-3.5" /> Hidden</>}
            </button>
          </div>
        )}
      </div>

      <div className="px-4 py-3">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={localText}
              onChange={e => setLocalText(e.target.value)}
              rows={5}
              data-testid={`cs-textarea-${sectionKey}`}
              className="w-full text-sm text-slate-700 border border-slate-300 rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/40 focus:border-[#2F80ED]"
            />
            <div className="flex gap-2">
              <button
                onClick={saveEdit}
                data-testid={`cs-save-${sectionKey}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#2F80ED] hover:bg-[#1a6fd4] text-white rounded-lg px-3 py-1.5 transition-colors"
              >
                <Save className="h-3.5 w-3.5" /> Save
              </button>
              <button
                onClick={cancelEdit}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className={cn(
            "text-sm leading-relaxed whitespace-pre-line",
            isEmpty ? "text-slate-400 italic" : "text-slate-700",
            !visible && "line-through text-slate-400",
          )}>
            {isEmpty ? "No content generated for this section." : (text || "")}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Evidence drawer ───────────────────────────────────────────────────────────

function EvidenceDrawer({ provenance, analysisId, transcriptId }) {
  const [open, setOpen] = useState(false);
  if (!provenance) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="cs-evidence-drawer-toggle"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          <Info className="h-4 w-4 text-slate-400" />
          Internal Evidence & Provenance
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-2">
            Staff-only · never shown to recipient
          </p>
          {[
            ["Transcript ID", provenance.transcript_id],
            ["Analysis ID", provenance.analysis_id || "None"],
            ["Estimate ID", provenance.estimate_id || "None"],
            ["Tax Profile at", provenance.tax_profile_at ? provenance.tax_profile_at.slice(0, 10) : "None"],
            ["Prior meetings used", provenance.prior_meetings_count ?? 0],
            ["Generated at", provenance.generated_at ? provenance.generated_at.slice(0, 16).replace("T", " ") : "—"],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between text-xs">
              <span className="text-slate-400">{label}</span>
              <span className="text-slate-600 font-mono truncate max-w-[200px]">{String(value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pricing confirmation modal ────────────────────────────────────────────────

function PricingConfirmModal({ pricingText, onConfirm, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="bg-white rounded-2xl shadow-2xl border border-amber-200 w-full max-w-lg mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="cs-pricing-confirm-modal"
      >
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">
              Verbally-quoted pricing is included
            </h3>
            <p className="text-xs text-amber-700 mt-1 leading-relaxed">
              This summary contains pricing that was discussed verbally during the consultation
              (Class A — not yet a formal estimate). Once approved, this content is locked.
              Confirm the quoted pricing is accurate before proceeding.
            </p>
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Pricing as drafted in summary
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line max-h-32 overflow-y-auto">
            {pricingText}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            If this is incorrect, cancel and edit the Pricing section before approving.
          </p>
        </div>
        <div className="px-6 pb-5 flex gap-2">
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="cs-pricing-confirm-approve-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 py-2.5 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Pricing is correct — Approve
          </button>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-xl px-4 py-2.5 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function SendModal({ summary, onSend, onCancel, busy }) {
  const [method, setMethod]       = useState("both");
  // Pre-populate from the canonical CRM email on the summary.
  // SendModal mounts fresh on each open (conditional render in parent), so
  // the initializer always uses the current summary's email — no stale state.
  const [email, setEmail]         = useState(summary.client_email || "");
  const [subject, setSubject]     = useState(
    `Consultation Summary — ${summary.meeting_date || ""}`
  );

  const noEmailAvailable = !email.trim() && !summary.client_email;

  const handleSend = () => {
    if (noEmailAvailable) return; // frontend guard; backend also validates
    onSend({ delivery_method: method, recipient_email: email.trim() || undefined, email_subject: subject.trim() || undefined });
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md mx-4 p-6 space-y-5"
        onClick={e => e.stopPropagation()}
        data-testid="cs-send-modal"
      >
        <div>
          <h3 className="text-base font-semibold text-slate-800">Send Consultation Summary</h3>
          <p className="text-sm text-slate-500 mt-1">To: {summary.client_name}</p>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-2">
            Delivery method
          </label>
          <div className="space-y-2">
            {[
              { v: "both",           label: "Email + PDF Attachment", icon: <><Mail className="h-4 w-4"/><Paperclip className="h-4 w-4"/></> },
              { v: "email",          label: "Email only (inline summary)", icon: <Mail className="h-4 w-4"/> },
              { v: "pdf_attachment", label: "PDF attachment + short email", icon: <Paperclip className="h-4 w-4"/> },
            ].map(opt => (
              <label
                key={opt.v}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors",
                  method === opt.v
                    ? "border-[#2F80ED] bg-blue-50"
                    : "border-slate-200 hover:border-slate-300",
                )}
              >
                <input
                  type="radio" name="method" value={opt.v}
                  checked={method === opt.v}
                  onChange={() => setMethod(opt.v)}
                  className="sr-only"
                />
                <span className={cn("flex items-center gap-1", method === opt.v ? "text-[#2F80ED]" : "text-slate-400")}>
                  {opt.icon}
                </span>
                <span className={cn("text-sm", method === opt.v ? "text-[#2F80ED] font-medium" : "text-slate-600")}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
            Recipient email <span className="text-slate-400 font-normal">(edit to override for this send only)</span>
          </label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter recipient email address"
            type="email"
            data-testid="cs-send-recipient-input"
            className={cn(
              "w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/40 focus:border-[#2F80ED]",
              noEmailAvailable ? "border-red-300 bg-red-50" : "border-slate-300",
            )}
          />
          {noEmailAvailable && (
            <p className="text-xs text-red-600 mt-1.5" data-testid="cs-send-no-email-warning">
              No email address is available for this contact. Please add an email to the contact record or enter one here.
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">
            Email subject
          </label>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            data-testid="cs-send-subject-input"
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2F80ED]/40 focus:border-[#2F80ED]"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={handleSend}
            disabled={busy || noEmailAvailable}
            data-testid="cs-send-confirm-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold bg-[#2F80ED] hover:bg-[#1a6fd4] text-white rounded-xl px-4 py-2.5 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending…" : "Send Now"}
          </button>
          <button
            onClick={onCancel}
            className="inline-flex items-center gap-2 text-sm text-slate-600 border border-slate-200 rounded-xl px-4 py-2.5 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ConsultationSummaryReview() {
  const { summaryId } = useParams();
  const navigate      = useNavigate();

  const [summary,     setSummary]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [busy,        setBusy]        = useState(false);
  const [showSend,    setShowSend]    = useState(false);
  const [showPricingConfirm, setShowPricingConfirm] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const [showPreview,   setShowPreview]   = useState(false);
  const [previewHtml,   setPreviewHtml]   = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/consultation-summaries/${summaryId}`);
      setSummary(data.summary);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load summary");
    } finally {
      setLoading(false);
    }
  }, [summaryId]);

  useEffect(() => { load(); }, [load]);

  // Poll every 3 s while Allison is generating
  useEffect(() => {
    if (!summary || summary.status !== "generating") return;
    const id = setInterval(() => {
      api.get(`/consultation-summaries/${summaryId}`)
        .then(({ data }) => { if (data.summary) setSummary(data.summary); })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [summary?.status, summaryId]);

  const handleToggleVisible = async (key) => {
    if (!summary || summary.status !== "draft") return;
    const vis = { ...(summary.section_visibility || {}), [key]: !summary.section_visibility?.[key] };
    setSummary(s => ({ ...s, section_visibility: vis }));
    try {
      const { data } = await api.put(`/consultation-summaries/${summaryId}`, { section_visibility: vis });
      setSummary(data.summary);
    } catch { toast.error("Failed to update visibility"); load(); }
  };

  const handleSaveEdit = async (key, value) => {
    if (!summary || summary.status !== "draft") return;
    const newSections = { ...(summary.sections || {}), [key]: value };
    setSummary(s => ({ ...s, sections: newSections }));
    try {
      const { data } = await api.put(`/consultation-summaries/${summaryId}`, { sections: newSections });
      setSummary(data.summary);
      toast.success("Section saved");
    } catch { toast.error("Failed to save section"); load(); }
  };

  const handleToggleFollowUp = async () => {
    if (!summary || summary.status !== "draft") return;
    const next = !summary.include_follow_up;
    setSummary(s => ({ ...s, include_follow_up: next }));
    try {
      const { data } = await api.put(`/consultation-summaries/${summaryId}`, { include_follow_up: next });
      setSummary(data.summary);
    } catch { toast.error("Failed to update setting"); load(); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setShowPreview(true);
    try {
      const resp = await api.get(`/consultation-summaries/${summaryId}/preview-html`, {
        responseType: "text",
      });
      setPreviewHtml(resp.data);
    } catch {
      setPreviewHtml("<p style='padding:2rem;color:#ef4444'>Could not load preview. Please try again.</p>");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApprove = async () => {
    const pricingText = ((summary?.sections || {}).pricing_discussed || "").trim();
    if (pricingText && !showPricingConfirm) {
      setShowPricingConfirm(true);
      return;
    }
    setShowPricingConfirm(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/consultation-summaries/${summaryId}/approve`);
      setSummary(data.summary);
      toast.success("Summary approved and ready to send");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); }
  };

  const handleRetry = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/consultation-summaries/${summaryId}/retry`);
      setSummary(data.summary);
      toast.success("Retrying generation — Allison is preparing the summary…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally { setBusy(false); }
  };

  const handleSend = async (sendBody) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/consultation-summaries/${summaryId}/send`, sendBody);
      setSummary(data.summary);
      setShowSend(false);
      toast.success(`Summary sent to ${data.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-slate-500">{error || "Summary not found"}</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-[#2F80ED] hover:underline">
          Go back
        </button>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[summary.status] || STATUS_CONFIG.draft;
  const sections   = summary.sections || {};
  const vis        = summary.section_visibility || {};
  const readonly   = summary.status !== "draft";

  const followUpText = sections.additional_follow_up_observation;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              data-testid="cs-back-btn"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-semibold text-slate-800">
                  Consultation Summary
                </h1>
                <span className={cn(
                  "inline-flex items-center text-[11px] font-semibold rounded-full px-2.5 py-1 border",
                  statusConf.color,
                )}>
                  {statusConf.label}
                </span>
                <span className="text-[11px] text-slate-400">v{summary.version}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {summary.client_name} · {summary.meeting_date} · {summary.meeting_title}
              </p>
            </div>
          </div>

          {/* ── Action bar ───────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {summary.status === "generation_failed" && (
              <button
                onClick={handleRetry}
                disabled={busy}
                data-testid="cs-retry-btn"
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 transition-colors disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Retry Generation
              </button>
            )}
            {summary.status === "draft" && (
              <>
                <button
                  onClick={() => navigate(`/meeting-review/${summary.transcript_id}`)}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Meeting
                </button>
                <button
                  onClick={handlePreview}
                  data-testid="cs-preview-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors font-medium"
                >
                  <Eye className="h-3.5 w-3.5" /> Preview Client Version
                </button>
                <button
                  onClick={handleApprove}
                  disabled={busy}
                  data-testid="cs-approve-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 py-2 transition-colors disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Approve
                </button>
              </>
            )}
            {summary.status === "approved" && (
              <>
                <span className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                  <Lock className="h-3.5 w-3.5" /> Content locked
                </span>
                <button
                  onClick={handlePreview}
                  data-testid="cs-preview-btn"
                  className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors font-medium"
                >
                  <Eye className="h-3.5 w-3.5" /> Preview Client Version
                </button>
                <button
                  onClick={() => setShowSend(true)}
                  data-testid="cs-send-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#2F80ED] hover:bg-[#1a6fd4] text-white rounded-xl px-4 py-2 transition-colors"
                >
                  <Send className="h-4 w-4" /> Send
                </button>
              </>
            )}
            {summary.status === "sent" && (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Sent to {summary.delivery?.recipient_email}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Generating state ──────────────────────────────────────────────── */}
        {summary.status === "generating" && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl px-6 py-10 text-center" data-testid="cs-generating-state">
            <Loader2 className="h-9 w-9 animate-spin text-indigo-500 mx-auto mb-4" />
            <p className="text-sm font-semibold text-indigo-800">Allison is preparing the Consultation Summary…</p>
            <p className="text-xs text-indigo-500 mt-1.5">You can leave this page — generation continues in the background and will be here when you return.</p>
          </div>
        )}

        {/* ── Generation failed state ───────────────────────────────────────── */}
        {summary.status === "generation_failed" && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-8" data-testid="cs-generation-failed-state">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-semibold text-red-800">Consultation Summary could not be generated.</p>
                {summary.error && (
                  <p className="text-xs text-red-600 font-mono">{summary.error}</p>
                )}
                <p className="text-xs text-red-500 mt-1">This is typically a transient issue. Use Retry to try again — it will not create a duplicate.</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Sent artifact record ─────────────────────────────────────────── */}
        {summary.status === "sent" && summary.delivery && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-4 space-y-1" data-testid="cs-sent-record">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Sent Record — Immutable</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {[
                ["Sent at",   (summary.delivery.sent_at || "").slice(0, 16).replace("T", " ")],
                ["Sent by",   summary.delivery.sent_by_name || "—"],
                ["Method",    summary.delivery.method || "—"],
                ["Recipient", summary.delivery.recipient_email || "—"],
                ["Fingerprint", (summary.content_fingerprint || "").slice(0, 12) + "…"],
                ["PDF fingerprint", summary.delivery.pdf_fingerprint ? (summary.delivery.pdf_fingerprint.slice(0, 12) + "…") : "—"],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-[10px] font-medium text-blue-500 uppercase tracking-wide">{label}</p>
                  <p className="text-xs text-blue-800 font-mono">{value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: sections ───────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-3">

            {!readonly && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-testid="cs-draft-notice">
                <Edit3 className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-amber-800">Draft — review before approving.</span>
                  <span className="text-amber-700"> Edit any section, hide irrelevant ones, then approve to lock content.</span>
                </div>
              </div>
            )}

            {RENDER_ORDER.map(key => (
              <SectionCard
                key={key}
                sectionKey={key}
                text={sections[key]}
                visible={vis[key] !== false}
                editing={!readonly}
                onToggleVisible={handleToggleVisible}
                onSaveEdit={handleSaveEdit}
                readonly={readonly}
              />
            ))}

            {/* ── Additional follow-up observation ─────────────────────── */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="cs-follow-up-section">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Additional Follow-Up Observation
                  </span>
                  <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 font-medium">
                    Optional · excluded by default
                  </span>
                </div>
                {!readonly && (
                  <button
                    onClick={handleToggleFollowUp}
                    data-testid="cs-toggle-follow-up"
                    className={cn(
                      "text-xs font-medium rounded-lg px-3 py-1.5 border transition-colors",
                      summary.include_follow_up
                        ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                        : "text-slate-500 border-slate-200 hover:border-slate-300",
                    )}
                  >
                    {summary.include_follow_up ? "Included in document" : "Add to document"}
                  </button>
                )}
              </div>
              <div className="px-4 py-3">
                <p className="text-[11px] text-slate-400 mb-2">
                  Post-meeting observations not discussed during the consultation.
                  Include only if you deliberately want to add follow-up guidance.
                </p>
                {followUpText ? (
                  <p className={cn(
                    "text-sm leading-relaxed text-slate-600",
                    !summary.include_follow_up && "opacity-50 line-through",
                  )}>
                    {followUpText}
                  </p>
                ) : (
                  <p className="text-sm text-slate-400 italic">Nothing generated for this section.</p>
                )}
              </div>
            </div>

            {/* ── Disclaimer ───────────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3" data-testid="cs-disclaimer-section">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                Professional Disclaimer
              </p>
              <p className="text-xs text-slate-500 leading-relaxed italic">
                {sections.disclaimer || ""}
              </p>
            </div>
          </div>

          {/* ── Right: sidebar ───────────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Status card */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Summary Status</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className={cn("text-xs font-semibold rounded-full px-2 py-0.5 border", statusConf.color)}>
                    {statusConf.label}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Version</span>
                  <span className="text-slate-700 font-medium">v{summary.version}</span>
                </div>
                {summary.approved_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Approved</span>
                    <span className="text-slate-700 text-xs">{summary.approved_at.slice(0, 10)}</span>
                  </div>
                )}
                {summary.content_fingerprint && (
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-slate-500 flex-shrink-0">Fingerprint</span>
                    <span className="text-slate-500 font-mono text-[10px] break-all">
                      {summary.content_fingerprint.slice(0, 16)}…
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Meeting info */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Meeting</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Client</span>
                  <span className="text-slate-700 font-medium text-right">{summary.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Date</span>
                  <span className="text-slate-700">{summary.meeting_date}</span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/meeting-review/${summary.transcript_id}`)}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs text-[#2F80ED] hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> View Meeting Record
              </button>
            </div>

            {/* Evidence drawer */}
            <EvidenceDrawer
              provenance={summary.evidence_provenance}
              analysisId={summary.analysis_id}
              transcriptId={summary.transcript_id}
            />

            {/* Section visibility summary */}
            {!readonly && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  Section Visibility
                </p>
                <div className="space-y-1">
                  {RENDER_ORDER.map(key => {
                    const isVisible = vis[key] !== false;
                    const hasContent = !!(sections[key] || "").trim();
                    return (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className={cn("text-slate-600 truncate", !hasContent && "text-slate-400")}>
                          {SECTION_LABELS[key]}
                        </span>
                        <span className={cn(
                          "font-medium ml-2 flex-shrink-0",
                          !hasContent ? "text-slate-300" : isVisible ? "text-emerald-600" : "text-slate-400",
                        )}>
                          {!hasContent ? "empty" : isVisible ? "shown" : "hidden"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Send modal ─────────────────────────────────────────────────────── */}
      {showSend && (
        <SendModal
          summary={summary}
          busy={busy}
          onSend={handleSend}
          onCancel={() => setShowSend(false)}
        />
      )}

      {/* ── Pricing confirmation modal ────────────────────────────────────── */}
      {/* ── Pricing confirmation modal ────────────────────────────────────── */}
      {showPricingConfirm && (
        <PricingConfirmModal
          pricingText={(summary?.sections?.pricing_discussed || "").trim()}
          busy={busy}
          onConfirm={handleApprove}
          onCancel={() => setShowPricingConfirm(false)}
        />
      )}

      {/* ── Client Preview modal ─────────────────────────────────────────────
          Read-only. No side effects. Reflects current persisted draft state.
          Renders using the same canonical section/visibility/label logic as
          PDF and email delivery. Hidden sections absent. No staff controls.
      ─────────────────────────────────────────────────────────────────────── */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-8 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPreview(false); }}
          data-testid="cs-preview-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden mb-8">
            {/* Modal header */}
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="space-y-1">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-base font-semibold text-slate-900">Client Preview</h2>
                  <span className="text-[11px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded px-2 py-0.5">
                    {(summary?.status || "draft").replace("_", " ")}
                  </span>
                </div>
                <p className="text-xs text-slate-500">
                  This is how the Consultation Summary will appear to the client. Hidden sections are absent. No staff controls.
                </p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                data-testid="cs-preview-close-btn"
                className="text-slate-400 hover:text-slate-700 transition-colors mt-0.5 flex-shrink-0 ml-4"
                aria-label="Close Preview"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Preview content — iframe with canonical HTML */}
            <div className="relative" style={{ height: "72vh" }}>
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                  <Loader2 className="h-7 w-7 animate-spin text-slate-400" />
                </div>
              )}
              {!previewLoading && (
                <iframe
                  srcDoc={previewHtml}
                  title="Client Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                  data-testid="cs-preview-iframe"
                />
              )}
            </div>

            {/* Modal footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-shrink-0">
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Read-only preview — draft status unchanged
              </p>
              <button
                onClick={() => setShowPreview(false)}
                data-testid="cs-preview-close-footer-btn"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg px-4 py-2 transition-colors"
              >
                <X className="h-4 w-4" /> Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
